import {
  BoundaryIdSchema,
  InvitationSchema,
  OrganizationIdSchema,
  ProjectIdSchema,
  RoleNameSchema,
  UserIdSchema,
} from "@storytell/types";
import z from "zod";

export const OrgIdParamsSchema = z.object({
  orgId: OrganizationIdSchema,
});

export const UpdateOrgNameInputSchema = z.object({
  name: z.string().trim().min(1).max(128),
});

export const UpdateOrgNameResponseSchema = z.object({
  organizationId: OrganizationIdSchema,
  organizationName: z.string(),
});

/** Off stops onboarding, inactivity and weekly email for every member, whatever their own settings say. */
export const OrgEngagementEmailSchema = z.object({ enabled: z.boolean() });

export const OrgMemberParamsSchema = OrgIdParamsSchema.extend({
  userId: UserIdSchema,
});

export const OrgInvitationParamsSchema = OrgIdParamsSchema.extend({
  invitationId: BoundaryIdSchema,
});

export const OrgDomainParamsSchema = OrgIdParamsSchema.extend({
  domainId: BoundaryIdSchema,
});

/** One shared domain a member's access traces back to. */
export const OrgMemberDomainSchema = z.object({
  domainId: BoundaryIdSchema,
  name: z.string(),
  projectId: ProjectIdSchema,
  /**
   * True when a grant of the member's own or their organization-wide role already ranks at or above
   * the domain, so the domain is not how they reach the project.
   */
  redundant: z.boolean(),
});

export const OrgMemberSchema = z.object({
  userId: UserIdSchema,
  email: z.string(),
  name: z.string(),
  /** Null for a member who reaches only the projects they were granted individually. */
  role: RoleNameSchema.nullable(),
  /** The organization's recorded owner, whose role the backend refuses to change or revoke. */
  rootOwner: z.boolean(),
  grantedAt: z.string(),
  /** Empty for someone only ever granted access by name. */
  domains: z
    .array(OrgMemberDomainSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type OrgMember = z.infer<typeof OrgMemberSchema>;

/**
 * An email domain a project is shared with. Everyone at the domain is admitted as they sign in, so
 * `members` keeps climbing while the entry stands and counts more people than `people` lists. The
 * role comes from the invitation the domain hangs off, which several domains can share, so the role
 * is displayed rather than editable per domain; revoking is the only action.
 */
export const OrgDomainSchema = z.object({
  domainId: BoundaryIdSchema,
  name: z.string(),
  projectId: ProjectIdSchema,
  projectName: z.string(),
  role: RoleNameSchema,
  members: z.number(),
  /** The people whose only access is this domain; they appear nowhere else in the response. */
  people: z
    .array(OrgMemberSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type OrgDomain = z.infer<typeof OrgDomainSchema>;

export const OrgMembersResponseSchema = z.object({
  members: z
    .array(OrgMemberSchema)
    .nullable()
    .transform((v) => v ?? []),
  domains: z
    .array(OrgDomainSchema)
    .nullable()
    .transform((v) => v ?? []),
});

/** The member's single organization-scope grant, which keeps reaching projects created after it was issued. */
export const OrgWideAccessSchema = z.object({
  role: RoleNameSchema,
  grantedAt: z.string(),
});

/** Where the effective role on a project came from: the organization-wide grant, a grant naming the project, or a project grant ranking above the organization-wide role. */
export const AccessSourceSchema = z.enum(["organization", "project", "raised"]);
export type AccessSource = z.infer<typeof AccessSourceSchema>;

/**
 * Roles are elevate-only, so a project grant ranked at or below the organization-wide role leaves
 * the effective role alone and is flagged redundant instead. `grantedAt` is null on a project
 * reached only through the organization-wide grant.
 */
export const ProjectAccessSchema = z.object({
  projectId: ProjectIdSchema,
  projectName: z.string(),
  role: RoleNameSchema,
  source: AccessSourceSchema,
  redundant: z.boolean(),
  grantedAt: z.string().nullable(),
  /** The project grant's own role, which `role` hides when the organization-wide one wins. */
  projectRole: RoleNameSchema.nullable(),
});
export type ProjectAccess = z.infer<typeof ProjectAccessSchema>;

export const OrgMemberAccessResponseSchema = z.object({
  userId: UserIdSchema,
  email: z.string(),
  name: z.string(),
  organization: OrgWideAccessSchema.nullable(),
  projects: z
    .array(ProjectAccessSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type OrgMemberAccess = z.infer<typeof OrgMemberAccessResponseSchema>;

export const OrgProjectsParamsSchema = OrgIdParamsSchema.extend({
  limit: z.number().optional(),
  afterId: z.string().optional(),
  q: z.string().optional(),
});

/** Members counts only the grants naming the project; an organization role reaches it without one. */
export const OrgProjectSummarySchema = z.object({
  projectId: ProjectIdSchema,
  name: z.string(),
  createdAt: z.string(),
  members: z.number(),
});
export type OrgProjectSummary = z.infer<typeof OrgProjectSummarySchema>;

export const OrgProjectsResponseSchema = z.object({
  data: z
    .array(OrgProjectSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
  first_id: z.string().optional(),
  last_id: z.string().optional(),
  has_more: z.boolean(),
});

export const OrgProjectParamsSchema = OrgIdParamsSchema.extend({
  projectId: ProjectIdSchema,
});

export const OrgProjectMemberSchema = z.object({
  userId: UserIdSchema,
  email: z.string(),
  name: z.string(),
  role: RoleNameSchema,
  grantedAt: z.string(),
});
export type OrgProjectMember = z.infer<typeof OrgProjectMemberSchema>;

/** Org-wide holds the people who reach the project through an organization role and no grant of their own. */
export const OrgProjectMembersResponseSchema = z.object({
  members: z
    .array(OrgProjectMemberSchema)
    .nullable()
    .transform((v) => v ?? []),
  orgWide: z
    .array(OrgProjectMemberSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type OrgProjectMembers = z.infer<typeof OrgProjectMembersResponseSchema>;

export const OrgMemberProjectParamsSchema = OrgMemberParamsSchema.extend({
  projectId: ProjectIdSchema,
});

/** Set when a shared domain the revoke cannot touch still reaches the member on that project. */
export const RevokeOrgMemberProjectResponseSchema = z.object({
  viaDomain: z.string().optional(),
});

/** The whole set is applied in a single transaction. */
export const GrantOrgMemberProjectsInputSchema = z.object({
  projectIds: z.array(ProjectIdSchema).min(1),
  role: RoleNameSchema,
});

export const SetOrgMemberRoleInputSchema = z.object({
  role: RoleNameSchema,
});

export const SetOrgMemberRoleResponseSchema = z.object({
  userId: UserIdSchema,
  role: RoleNameSchema,
});

/** A domain rule is rejected at organization scope, so an invite names addresses only. */
export const CreateOrgInvitationInputSchema = z.object({
  role: RoleNameSchema,
  users: z.array(z.object({ email: z.string() })).min(1),
});

export const OrgInvitationsResponseSchema = z
  .array(InvitationSchema)
  .nullable()
  .transform((v) => v ?? []);

export const AuditAreaSchema = z.enum(["general", "billing", "integrations", "members"]);
export type AuditArea = z.infer<typeof AuditAreaSchema>;

export const AuditCategorySchema = z.enum(["security", "business", "system"]);
export type AuditCategory = z.infer<typeof AuditCategorySchema>;

/** `member` carries the member's user id; `invitation` carries the invitation id. */
export const AuditResourceTypeSchema = z.enum([
  "google_integration",
  "notion_integration",
  "slack_integration",
  "linear_integration",
  "github_integration",
  "member",
  "invitation",
  "domain",
  "chief_session",
]);
export type AuditResourceType = z.infer<typeof AuditResourceTypeSchema>;

/** Action stays a plain string, not an enum: rows outlive the clients reading them, so an unknown action must parse. */
export const AuditEntrySchema = z.object({
  id: z.string(),
  organizationId: OrganizationIdSchema,
  projectId: z.string().nullable(),
  category: z.string(),
  area: z.string(),
  action: z.string(),
  actorType: z.string(),
  actorId: z.string(),
  actorEmail: z.string().nullable(),
  actorName: z.string().nullable(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  payload: z.unknown(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

export const AuditEventsResponseSchema = z.object({
  data: z
    .array(AuditEntrySchema)
    .nullable()
    .transform((v) => v ?? []),
  first_id: z.string().optional(),
  last_id: z.string().optional(),
  has_more: z.boolean(),
});

export const AuditEventsParamsSchema = z.object({
  orgId: OrganizationIdSchema,
  limit: z.number().optional(),
  afterId: z.string().optional(),
  area: AuditAreaSchema.optional(),
  actorId: z.string().optional(),
  category: AuditCategorySchema.optional(),
  resourceType: AuditResourceTypeSchema.optional(),
  resourceId: z.string().optional(),
});

/** Plain strings, not the enums: one value this client predates would fail the whole response, and the filters would fall back to offering everything. */
export const AuditFacetCombinationSchema = z.object({
  area: z.string(),
  category: z.string(),
  resourceType: z.string().nullable(),
});
export type AuditFacetCombination = z.infer<typeof AuditFacetCombinationSchema>;

export const AuditFacetActorSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  isSystem: z.boolean(),
});
export type AuditFacetActor = z.infer<typeof AuditFacetActorSchema>;

/** Combinations are the area/category/resourceType triples the org's trail actually holds, so a filter can offer only reachable values. */
export const AuditFacetsResponseSchema = z.object({
  combinations: z
    .array(AuditFacetCombinationSchema)
    .nullable()
    .transform((v) => v ?? []),
  actors: z
    .array(AuditFacetActorSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type AuditFacets = z.infer<typeof AuditFacetsResponseSchema>;

/** Fields the writer omits are absent rather than null, so they are optional and not nullable. */
/** Narrow object rather than a loose record: these values reach the sentence catalogue as interpolation params and need a real type. */
export const AuditOrgRenamedPayloadSchema = z.object({
  name: z.string(),
  previousAttributes: z.object({ name: z.string() }),
});
export const AuditCheckoutStartedPayloadSchema = z.object({
  kind: z.string(),
  plan: z.string().optional(),
  credits: z.number().optional(),
});
export const AuditThresholdsChangedPayloadSchema = z.object({ percents: z.array(z.number()) });
export const AuditContactsChangedPayloadSchema = z.object({ added: z.number(), removed: z.number() });
export const AuditSubscriptionActivatedPayloadSchema = z.object({ plan: z.string(), seats: z.number() });
export const AuditPlanChangedPayloadSchema = z.object({
  plan: z.string(),
  previousAttributes: z.object({ plan: z.string() }),
});
export const AuditSubscriptionCancelledPayloadSchema = z.object({ plan: z.string() });
export const AuditPaymentFailedPayloadSchema = z.object({
  amountCents: z.number(),
  currency: z.string(),
});
export const AuditCreditsPayloadSchema = z.object({ credits: z.number() });
export const AuditIntegrationPayloadSchema = z.object({ provider: z.string() });
export const AuditSyncScopeChangedPayloadSchema = z.object({
  provider: z.string(),
  mode: z.string(),
  roots: z.number(),
});
export const AuditIntegrationProjectPayloadSchema = z.object({ provider: z.string(), projectName: z.string() });
export const AuditSubjectChangedPayloadSchema = z.object({ provider: z.string(), subject: z.string() });
export const AuditMemberRoleChangedPayloadSchema = z.object({
  member: z.string(),
  role: z.string(),
  previousAttributes: z.object({ role: z.string() }).optional(),
});
export const AuditMemberRemovedPayloadSchema = z.object({ member: z.string() });
/** One bulk apply is one event, so `projects` holds every name it touched. A revoke names no role. */
export const AuditMemberProjectAccessPayloadSchema = z.object({
  member: z.string(),
  role: z.string().optional(),
  projects: z.array(z.string()),
});
/** Both values are snapshots: the domain row is closed by the same act, and the project can be renamed later. */
export const AuditDomainAccessRevokedPayloadSchema = z.object({
  domain: z.string(),
  project: z.string(),
});
/**
 * One person's access to one session. `member` is whose access changed, which is not the actor when someone else
 * removed it. Session and project are name snapshots; a session that was never named snapshots as empty.
 */
export const AuditSessionAccessPayloadSchema = z.object({
  member: z.string(),
  session: z.string(),
  project: z.string(),
  reason: z.enum([
    "link_redeemed",
    "calendar_invited",
    "link_revoked",
    "link_downgraded",
    "session_deleted",
    "calendar_uninvited",
  ]),
});
/** An organization invitation names one or more addresses, so sent and revoked both carry a list. */
export const AuditInvitationPayloadSchema = z.object({ role: z.string(), emails: z.array(z.string()) });
export const AuditInvitationAcceptedPayloadSchema = z.object({ role: z.string() });
